import { useEffect, useState } from "react";
import CookieConsent from "react-cookie-consent";
import Switch from "@mui/material/Switch";
import { Button, Box } from "@mui/material";
import styles from "./CookieBanner.module.scss";
import {
  defaultConsent,
  getStoredConsent,
  saveConsent,
  acceptAllConsent,
  rejectAllConsent,
  hasConsentBeenGiven,
} from "./cookieConsent.helpers";
import { loadAllowedTrackers } from "./trackerLoader";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

export default function CookieBanner() {
  const { pathname } = useLocation();
  const { t } = useTranslation();

  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [settings, setSettings] = useState(defaultConsent);

  useEffect(() => {
    const stored = getStoredConsent();

    if (stored) {
      setSettings(stored);
    }

    const isPrivacyPage = pathname === "/polityka-prywatnosci";

    if (!hasConsentBeenGiven() && !isPrivacyPage) {
      setShowBanner(true);
    }
  }, [pathname]);

  useEffect(() => {
    if (showBanner || showPreferences) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [showBanner, showPreferences]);

  const handleOpenPreferences = () => {
    const stored = getStoredConsent();
    if (stored) {
      setSettings(stored);
    }
    setShowPreferences(true);
  };

  const handleClosePreferences = () => {
    setShowPreferences(false);
    if (!hasConsentBeenGiven()) {
      setShowBanner(true);
    }
  };

  const handleAcceptAll = () => {
    acceptAllConsent();
    const stored = getStoredConsent();

    if (stored) {
      setSettings(stored);
    }

    loadAllowedTrackers();
    setShowBanner(false);
    setShowPreferences(false);
  };

  const handleRejectAll = () => {
    rejectAllConsent();
    const stored = getStoredConsent();

    if (stored) {
      setSettings(stored);
    }

    setShowBanner(false);
    setShowPreferences(false);
  };

  const handleAcceptSelected = () => {
    saveConsent(settings);
    loadAllowedTrackers();
    setShowBanner(false);
    setShowPreferences(false);
  };

  const handleToggle = (key) => (event) => {
    setSettings((prev) => ({
      ...prev,
      [key]: event.target.checked,
    }));
  };

  if (!showBanner && !showPreferences) {
    return null;
  }

  return (
    <>
      {!showPreferences && showBanner && (
        <CookieConsent
          disableStyles
          buttonText=""
          enableDeclineButton={false}
          visible="show"
          containerClasses={styles.overlay}
          contentClasses={styles.contentReset}
          buttonStyle={{ display: "none" }}
        >
          <div className={styles.modal}>
            <h3 className={styles.title}>{t("cookieBanner.title")}</h3>

            <p className={styles.text}>
              {t("cookieBanner.text1")}
            </p>

            <p className={styles.text}>
              {t("cookieBanner.text2a")}{" "}
              <a href="/polityka-prywatnosci" className={styles.link}>
                {t("cookieBanner.privacyPolicy")}
              </a>
              {t("cookieBanner.text2b")}
            </p>

            <p className={styles.text}>
              {t("cookieBanner.text3")}
            </p>

            <div className={styles.actions}>
              <Button
                type="button"
                className={styles.btnOutline}
                onClick={handleOpenPreferences}
              >
                {t("cookieBanner.customize")}
              </Button>

              <Button
                type="button"
                className={styles.btnOutline}
                onClick={handleRejectAll}
              >
                {t("cookieBanner.rejectAll")}
              </Button>

              <Button
                type="button"
                className={styles.btnPrimary}
                onClick={handleAcceptAll}
              >
                {t("cookieBanner.acceptAll")}
              </Button>
            </div>
          </div>
        </CookieConsent>

      )}

      {showPreferences && (
        <Box className={styles.overlay}>
          <div className={`${styles.modal} ${styles.preferencesModal}`}>
            <h2 className={styles.title}>{t("cookieBanner.preferencesTitle")}</h2>

            <div className={styles.optionList}>
              <div className={styles.optionRow}>
                <div className={styles.optionText}>
                  <h3>{t("cookieBanner.options.necessary.title")}</h3>
                  <p>
                    {t("cookieBanner.options.necessary.description")}
                  </p>
                </div>

                <Switch checked color="success" className={styles.switchDisabled} />
              </div>

              <div className={styles.optionRow}>
                <div className={styles.optionText}>
                  <h3>{t("cookieBanner.options.analytics.title")}</h3>
                  <p>
                    {t("cookieBanner.options.analytics.description")}
                  </p>
                </div>

                <Switch
                  className={styles.customSwitch}
                  checked={settings.analytics}
                  onChange={handleToggle("analytics")}
                  color="success"
                />
              </div>

              <div className={styles.optionRow}>
                <div className={styles.optionText}>
                  <h3>{t("cookieBanner.options.functional.title")}</h3>
                  <p>
                    {t("cookieBanner.options.functional.description")}
                  </p>
                </div>

                <Switch
                  checked={settings.functional}
                  onChange={handleToggle("functional")}
                  color="success"
                />
              </div>

              <div className={styles.optionRow}>
                <div className={styles.optionText}>
                  <h3>{t("cookieBanner.options.marketing.title")}</h3>
                  <p>
                    {t("cookieBanner.options.marketing.description")}
                  </p>
                </div>

                <Switch
                  checked={settings.marketing}
                  onChange={handleToggle("marketing")}
                  color="success"
                />
              </div>

              <div className={styles.optionRow}>
                <div className={styles.optionText}>
                  <h3>{t("cookieBanner.options.externalMedia.title")}</h3>
                  <p>
                    {t("cookieBanner.options.externalMedia.description")}
                  </p>
                </div>

                <Switch
                  checked={settings.externalMedia}
                  onChange={handleToggle("externalMedia")}
                  color="success"
                />
              </div>
            </div>

            <div className={styles.actions}>
              <Button
                type="button"
                className={styles.closePreferences}
                onClick={handleClosePreferences}
              >
                {t("common.back")}
              </Button>
              <Button
                type="button"
                className={styles.btnOutline}
                onClick={handleAcceptAll}
              >
                {t("cookieBanner.acceptAll")}
              </Button>

              <Button
                type="button"
                className={styles.btnPrimary}
                onClick={handleAcceptSelected}
              >
                {t("cookieBanner.acceptSelected")}
              </Button>

            </div>
          </div>
        </Box>
      )}
    </>
  );
}
